import { PATTERNS } from "./patterns";
import { makePath } from "./utils";

export const routes = {
  // =========================
  // Public / Auth
  // =========================
  home: makePath(PATTERNS.home),

  auth: {
    login: makePath(PATTERNS.login),
    forgotPassword: makePath(PATTERNS.forgotPassword),
    createNewPassword: makePath(PATTERNS.createNewPassword),
    signUp: makePath(PATTERNS.signUp),
  },

  invitation: makePath(PATTERNS.invitation),
  selectTeam: makePath(PATTERNS.selectTeam),

  onboarding: {
    platform: makePath(PATTERNS.onboardingPlatform),
    survey: makePath(PATTERNS.onboardingSurvey),
  },
  autoptoOffer: makePath(PATTERNS.autoptoOffer),

  // Auth-adjacent: these are reachable while authenticated (verifyMfa) or via
  // an email token (unlock), and intentionally live outside `auth.*` because
  // they are not gated by `GuestOnlyRoute` like the other auth pages.
  verifyMfa: makePath(PATTERNS.verifyMfa),
  unlock: makePath(PATTERNS.unlock),
  demoLogin: makePath(PATTERNS.demoLogin),
  activateAccount: makePath(PATTERNS.activateAccount),

  // =========================
  // Global protected pages
  // =========================
  apiKeys: makePath(PATTERNS.apiKeys),
  teamMembers: makePath(PATTERNS.teamMembers),
  notifications: makePath(PATTERNS.notifications),

  settings: {
    root: makePath(PATTERNS.settings),
    tab: makePath(PATTERNS.settingsTab),
  },

  // =========================
  // Sites (main section)
  // =========================

  site: {
    root: makePath(PATTERNS.siteRoot),

    wizard: makePath(PATTERNS.newSiteWizard), // routes.site.wizard({ tab: "new"/etc... })

    settings: {
      root: makePath(PATTERNS.siteSettings),
      tab: makePath(PATTERNS.siteSettingsTab),
    },

    pages: makePath(PATTERNS.sitePages),

    liveEditor: {
      install: makePath(PATTERNS.siteLiveEditorInstall),
      customizations: makePath(PATTERNS.siteLiveEditorCustomizations),
    },

    links: {
      overview: makePath(PATTERNS.siteLinksOverview),

      niche: makePath(PATTERNS.siteLinksNiche),
      nicheTask: makePath(PATTERNS.siteLinksNicheTask),

      outreach: makePath(PATTERNS.siteLinksOutreach),
      outreachTask: makePath(PATTERNS.siteLinksOutreachTask),

      brand: makePath(PATTERNS.siteLinksBrand),
      brandTask: makePath(PATTERNS.siteLinksBrandTask),
    },

    keywords: {
      list: makePath(PATTERNS.siteKeywordsList),
      keyword: makePath(PATTERNS.siteKeyword),
      imports: makePath(PATTERNS.siteKeywordImports),
      import: makePath(PATTERNS.siteKeywordImport),
    },

    reports: {
      root: makePath(PATTERNS.siteReports),
      builtLinks: makePath(PATTERNS.siteReportsBuiltLinks),
      keywordRanking: makePath(PATTERNS.siteReportsKeywordRanking),
      siteSales: makePath(PATTERNS.siteReportsSiteSales),
      recommendationExports: makePath(
        PATTERNS.siteReportsRecommendationExports,
      ),
      workDone: makePath(PATTERNS.siteReportsWorkDone),
      client: makePath(PATTERNS.siteReportsClient),
    },

    onpage: makePath(PATTERNS.siteOnpage),
    content: makePath(PATTERNS.siteContent),
    schema: makePath(PATTERNS.siteSchema),
    aiInstructions: makePath(PATTERNS.siteAiInstructions),
    interlinks: makePath(PATTERNS.siteInterlinks),

    googleDashboard: makePath(PATTERNS.siteGoogleDashboard),
    trafficMetrics: makePath(PATTERNS.siteTrafficMetrics),
    visibilityMetrics: makePath(PATTERNS.siteVisibilityMetrics),
    visibilityEngineSettings: makePath(PATTERNS.siteVisibilityEngineSettings),
    visibilityEngineSettingsTab: makePath(
      PATTERNS.siteVisibilityEngineSettingsTab,
    ),

    // Visibility Engine domains module mirrored into the site context
    visibilityEngine: {
      domain: makePath(PATTERNS.siteVisibilityEngineDomain),
      about: makePath(PATTERNS.siteVisibilityEngineAbout),
    },
  },

  // =========================
  // Visibility Engine (site speed)
  // =========================
  visibilityEngine: {
    domains: makePath(PATTERNS.visibilityEngineDomains),
    domain: makePath(PATTERNS.visibilityEngineDomain),

    wizard: {
      setup: makePath(PATTERNS.visibilityEngineWizardSetup),
      verification: makePath(PATTERNS.visibilityEngineWizardVerification),
      certificate: makePath(PATTERNS.visibilityEngineWizardCertificate),
      dns: makePath(PATTERNS.visibilityEngineWizardDns),
      ns: makePath(PATTERNS.visibilityEngineWizardNs),
      routing: makePath(PATTERNS.visibilityEngineWizardRouting),
    },
  },

  // =========================
  // Services
  // =========================
  services: {
    root: makePath(PATTERNS.services),
    orders: makePath(PATTERNS.servicesOrders),
    brandLinks: makePath(PATTERNS.servicesBrandLinks),
    nicheLinks: makePath(PATTERNS.servicesNicheLinks),
    conciergeInstallation: makePath(PATTERNS.servicesConciergeInstallation),
    llm_citations: makePath(PATTERNS.llm_citations),
    communityLinks: makePath(PATTERNS.servicesCommunityLinks),
  },

  // =========================
  // Billing
  // =========================
  billing: {
    root: makePath(PATTERNS.billing),
    subscription: makePath(PATTERNS.billingSubscription),
    subscriptionEdit: makePath(PATTERNS.billingSubscriptionEdit),
    invoices: makePath(PATTERNS.billingInvoices),
  },

  // =========================
  // Personal subscription links
  // =========================
  subscribe: makePath(PATTERNS.subscribe),
} as const;
